import { bankTransfer } from "~/assets/assets";
import {
  HubbleRoute,
  PageAlignment,
  pushPage,
  goBack,
  popUntilHome,
} from "~/shared_states/modal";
import { ButtonRegular } from "~/widgets/button";
import { Show, createSignal } from "solid-js";
import { SummaryItem } from "../summary_modal/summary_modal";
import { TransferModeDetails } from "./transfer_mode_details";
import { toRupees } from "~/utils/number";
import { VoucherCart } from "../voucher_edit/voucher";
import { TransactionDetail } from "~/screens/transactions/transaction_detail";
import { RedemptionTransactions } from "~/screens/transactions/transactions";
import { snackApiCallWrapper } from "~/shared_states/snackbar";
import { initPgRedemption } from "~/data/products";
import {
  VoucherGenerationFailed,
  VoucherGenLoader,
} from "../payment_options/payment_options";
import { VoucherGenSuccess } from "../payment_options/voucher_gen_success";
import {
  DiscountDetails,
  SimulatedTransactionDetails,
} from "~/server/types/brand";
import { generateDeviceVerificationToken } from "~/utils/common";
import { Header } from "~/types";
import { useClassicContext } from "~/data/classic/classic_context";

export function SummaryTransferModal(props: {
  amount: number;
  taxAmount: number;
  redeemAmount: number;
  isGetThisCard?: boolean;
  type: "voucher" | "payout";
  cart?: VoucherCart;
  dryRunTransaction?: () => SimulatedTransactionDetails;
  discountDetails?: () => DiscountDetails | undefined;
  totalRequiredCoins?: () => number;
}) {
  const [isGenerating, setIsGenerating] = createSignal(false);
  const { data } = useClassicContext();
  const v4Home = data.isLoggedIn ? data.v4HomePage : null;
  const fraudChecksEnabled = v4Home?.user.fraudChecksEnabled;

  return (
    <div class="relative flex h-full w-full flex-col items-center justify-start bg-baseTertiaryLight px-5 py-4">
      <div class="h-1 w-[45px] rounded-[22px] bg-baseTertiaryDark" />
      <img
        src={
          props.type === "voucher"
            ? props.cart?.voucherProduct!.voucherProductMetadata.logoUrl
            : bankTransfer
        }
        class="mb-3 mt-6 h-[72.19px] w-[72.19px] self-start rounded-full"
        alt="Bank Transfer"
      />

      <Show
        when={props.type === "payout"}
        fallback={
          <h1 class="mb-5 self-start text-h3 text-textDark">
            {toRupees(props.amount)} {props.cart?.voucherProduct?.name} card
          </h1>
        }
      >
        <h1 class="mb-5 self-start text-h3 text-textDark">
          Transfer {toRupees(props.amount)} to bank{" "}
        </h1>
      </Show>

      <div class="w-full px-0 py-4">
        <div class="caption-overline mb-4 text-f12w700 uppercase text-textNormal">
          Summary
        </div>
        <div class="mb-2.5 h-px border-t border-y-baseTertiaryMedium" />
        <SummaryItem
          class="text-bold text-textDark"
          label={props.type === "payout" ? "Transfer amount" : "Card price"}
          value={
            props.type === "payout"
              ? toRupees(props.amount ?? 0)
              : toRupees(props.cart?.amount ?? 0)
          }
        />
        <div class="flex flex-row items-end justify-between px-0 py-1.5 text-bold text-textDark">
          <div>
            <p>Taxes</p>
            <p class="text-f12 text-textNormal">194(R) adjusted</p>
          </div>
          <p class="text-textDark">{toRupees(props.taxAmount)}</p>
        </div>

        <Show when={true}>
          <div class="mb-2.5 h-px border-t border-y-baseTertiaryMedium" />
          <SummaryItem
            class="text-bold text-textDark"
            label="You’ll redeem"
            isCoin={true}
            value={toRupees(props.redeemAmount)}
          />
        </Show>
      </div>
      <ButtonRegular
        class="mt-5 w-full"
        isEnabled={!isGenerating()}
        isLoading={isGenerating()}
        onClick={async () => {
          if (props.type === "payout") {
            pushPage(
              () => <TransferModeDetails amount={props.amount} />,
              {
                isModal: false,
                alignment: PageAlignment.bottom,
                offset: { top: 20, right: 20 },
              },
              HubbleRoute.BANK_TRANSFER_MODE
            );
          } else if (props.type === "voucher" && props.cart) {
            setIsGenerating(true);
            try {
              const res = await snackApiCallWrapper(
                initPgRedemption({
                  ...(fraudChecksEnabled && {
                    headers: {
                      [Header.FpDeviceToken]:
                        await generateDeviceVerificationToken(),
                    },
                  }),
                  params: {
                    amount: props.cart?.amount,
                    paymentMode: props.dryRunTransaction?.()?.paymentMode!,
                    productId: props.cart.voucherProductId,
                    denominationDetails: props.cart.denominations?.map((d) => ({
                      denomination: d.denomination,
                      quantity: d.quantity,
                    })),
                    variantId: props.cart.variantId,
                    consumeCoins: props.cart.useCoins,
                    couponCode: props.cart.couponCode,
                  },
                })
              );

              setIsGenerating(false);

              goBack(() => {
                pushPage(
                  () => (
                    <VoucherGenLoader
                      onVoucherGenerated={async (order, donationDetails) => {
                        if (order.status == "COMPLETED") {
                          await popUntilHome();
                          pushPage(
                            () => <RedemptionTransactions />,
                            undefined,
                            HubbleRoute.TRANSACTIONS
                          );
                          pushPage(
                            () => (
                              <TransactionDetail
                                transactionId={res.id}
                                type="redemption"
                              />
                            ),
                            undefined,
                            HubbleRoute.TRANSACTION_L2
                          );
                          pushPage(
                            () => (
                              <VoucherGenSuccess
                                orderId={order.id}
                                orderDetails={order}
                                onClose={() => goBack()}
                                refreshProfile={true}
                                donationDetails={donationDetails}
                              />
                            ),
                            undefined,
                            HubbleRoute.VOUCHER_GENERATION_SUCCESS
                          );
                        }

                        if (order.status == "FAILED") {
                          goBack(() => {
                            pushPage(
                              () => <VoucherGenerationFailed />,
                              undefined,
                              HubbleRoute.VOUCHER_GENERATION_FAIL
                            );
                          });
                        }
                      }}
                      orderId={res.storeOrderDetails.id}
                      transactionId={res.id}
                    />
                  ),
                  undefined,
                  HubbleRoute.VOUCHER_GENERATION_LOADER
                );
              });
            } catch (error) {
              setIsGenerating(false);
              console.error("Voucher generation failed:", error);
            }
          }
        }}
      >
        <Show when={props.isGetThisCard} fallback={"Got it"}>
          Confirm & get this card
        </Show>
      </ButtonRegular>
    </div>
  );
}
